'use client';

import React, { useState } from 'react';
import { Mail, MessageCircle, RefreshCw, Pause, Play } from 'lucide-react';
import { toast } from 'sonner';
import StatusBadge from '@/components/ui/StatusBadge';
import Icon from '@/components/ui/AppIcon';


const campaigns = [
  {
    id: 'camp-001',
    name: 'Campaña Email Bienvenida',
    description: 'Secuencia de 5 emails para nuevos leads — presenta Myfinder MF y dossiers con aportación voluntaria de €0.50 para el mantenimiento del servicio',
    type: 'email' as const,
    status: 'active' as const,
    sent: 234,
    openRate: 18.4,
    replyRate: null,
    conversions: 22,
    revenue: 11.0,
    progress: 78,
    leads: 234,
    lastSent: 'hace 3 min',
  },
  {
    id: 'camp-002',
    name: 'WhatsApp Dossier Viaje — Aportación €0.50',
    description: 'Mensajes de WhatsApp ofreciendo dossiers completos de viaje con aportación voluntaria de €0.50 para el mantenimiento del servicio',
    type: 'whatsapp' as const,
    status: 'active' as const,
    sent: 89,
    openRate: null,
    replyRate: 34.1,
    conversions: 38,
    revenue: 19.0,
    progress: 62,
    leads: 89,
    lastSent: 'hace 8 min',
  },
  {
    id: 'camp-003',
    name: 'Retargeting Colaboradores IA',
    description: 'Reactivación de leads fríos con IA — ofrece dossiers adicionales a colaboradores previos del servicio',
    type: 'retargeting' as const,
    status: 'active' as const,
    sent: 45,
    openRate: 31.2,
    replyRate: null,
    conversions: 12,
    revenue: 6.0,
    progress: 45,
    leads: 45,
    lastSent: 'hace 15 min',
  },
];

const typeConfig = {
  email: {
    icon: Mail,
    label: 'Email',
    color: 'text-primary',
    bg: 'bg-primary/10',
    border: 'border-primary/20',
  },
  whatsapp: {
    icon: MessageCircle,
    label: 'WhatsApp',
    color: 'text-accent',
    bg: 'bg-accent/10',
    border: 'border-accent/20',
  },
  retargeting: {
    icon: RefreshCw,
    label: 'Retargeting',
    color: 'text-warning',
    bg: 'bg-warning/10',
    border: 'border-warning/20',
  },
};

export default function CampaignCards() {
  const [pausedIds, setPausedIds] = useState<Set<string>>(new Set());

  const toggleCampaign = (id: string, name: string) => {
    setPausedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        toast.success(`"${name}" reactivada`);
      } else {
        next.add(id);
        toast.warning(`"${name}" pausada`);
      }
      return next;
    });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-4">
      {campaigns.map((camp) => {
        const cfg = typeConfig[camp.type];
        const Icon = cfg.icon;
        const isPaused = pausedIds.has(camp.id);

        return (
          <div
            key={camp.id}
            className={`bg-card border rounded-xl p-5 card-hover ${cfg.border}`}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="flex items-center gap-2.5">
                <span className={`p-2 rounded-lg ${cfg.bg}`}>
                  <Icon size={15} className={cfg.color} />
                </span>
                <div>
                  <p className="text-xs font-semibold text-foreground leading-tight">
                    {camp.name}
                  </p>
                  <span className="text-2xs text-muted-foreground">{cfg.label}</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <StatusBadge variant={isPaused ? 'paused' : 'active'} />
                <button
                  onClick={() => toggleCampaign(camp.id, camp.name)}
                  className={`p-1.5 rounded-lg transition-all duration-150 hover:scale-105 active:scale-95 ${
                    isPaused
                      ? 'bg-accent/10 text-accent hover:bg-accent/20' :'bg-warning/10 text-warning hover:bg-warning/20'
                  }`}
                  title={isPaused ? 'Reactivar campaña' : 'Pausar campaña'}
                >
                  {isPaused ? <Play size={12} /> : <Pause size={12} />}
                </button>
              </div>
            </div>

            <p className="text-2xs text-muted-foreground mb-4 leading-relaxed">
              {camp.description}
            </p>

            {/* Metrics grid */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="text-center">
                <p className="text-sm font-bold font-tabular text-foreground">
                  {camp.sent}
                </p>
                <p className="text-2xs text-muted-foreground">Enviados</p>
              </div>
              <div className="text-center">
                <p className={`text-sm font-bold font-tabular ${cfg.color}`}>
                  {camp.openRate !== null
                    ? `${camp.openRate}%`
                    : `${camp.replyRate}%`}
                </p>
                <p className="text-2xs text-muted-foreground">
                  {camp.openRate !== null ? 'Apertura' : 'Respuesta'}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm font-bold font-tabular text-accent">
                  {camp.conversions}
                </p>
                <p className="text-2xs text-muted-foreground">Aportaciones</p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mb-3">
              <div className="flex justify-between text-2xs text-muted-foreground mb-1">
                <span>Progreso del embudo</span>
                <span className="font-semibold text-foreground">{camp.progress}%</span>
              </div>
              <div className="w-full h-1.5 bg-secondary rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-700 ${
                    camp.type === 'email' ?'bg-primary'
                      : camp.type === 'whatsapp' ?'bg-accent' :'bg-warning'
                  }`}
                  style={{ width: `${camp.progress}%` }}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-3 border-t border-border/50">
              <span className="text-2xs text-muted-foreground">
                Aportaciones recibidas:{' '}
                <span className="font-semibold text-warning font-tabular">
                  €{camp.revenue.toFixed(2)}
                </span>
              </span>
              <span className="text-2xs text-muted-foreground">
                Último envío: {camp.lastSent}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}